import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { ErrorPage, Button } from "@repo/ping-design";
import { toast } from "react-hot-toast";
import { getApiBaseUrl } from "../services/utils";
import "./EmailResendErrorPage.scss";

/**
 * EmailResendErrorPage component for handling token expired errors
 * with email resend functionality.
 */
const EmailResendErrorPage: React.FC = () => {
  const location = useLocation();
  const [isResending, setIsResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Extract token and sovId from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("access_token");
  const sovId = searchParams.get("sovId");
  const messageType = searchParams.get("message");

  // Determine the appropriate message and button state based on message type
  const getMessageContent = () => {
    if (emailSent) {
      return {
        mainText: "New link is being sent",
        message: "A new link is being sent to the original email address.",
        showButton: false,
      };
    }

    switch (messageType) {
      case "recent_resend":
        return {
          mainText: "Link already reissued recently",
          message:
            "A new link has already been sent recently. Please check your email.",
          showButton: false,
        };
      case "auto_resending":
        return {
          mainText: "New link is being sent",
          message: "A new link is being sent to the original email address.",
          showButton: false,
        };
      default:
        return {
          mainText: "",
          message: "The link from your email has expired",
          showButton: true,
        };
    }
  };

  const messageContent = getMessageContent();

  const handleResendEmail = async () => {
    if (!token || !sovId) {
      console.error("Missing required parameters:", { token, sovId });
      toast.error("Missing required parameters for email resend");
      return;
    }

    setIsResending(true);
    try {
      // Make POST request to resend email endpoint with token as query parameter
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}api/v1/sov/${sovId}/resend_email?access_token=${encodeURIComponent(token)}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setEmailSent(true);
        toast.success("Email sent successfully! Please check your inbox.");
      } else {
        const errorData = await response.json().catch(() => null);
        toast.error(
          errorData?.message || "Failed to send email. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error resending email:", error);
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <ErrorPage type="CUSTOM" mainText={messageContent.mainText}>
      <div className="EmailResendErrorPage__Content">
        <h1 className="EmailResendErrorPage__Title">
          Thank you for visiting Ping.Extraction!
        </h1>
        <h2 className="EmailResendErrorPage__Subtitle">
          The most advanced unstructured data tool for commercial property
          insurance
        </h2>
        {messageContent.message && (
          <p className="EmailResendErrorPage__Message">
            {messageContent.message}
          </p>
        )}
      </div>

      <div className="EmailResendErrorPage__ButtonContainer">
        {messageContent.showButton && (
          <Button
            label={
              isResending
                ? "Sending..."
                : "Click here to get a new link by email"
            }
            onClick={handleResendEmail}
            disabled={isResending}
            variant="brand"
            iconName={isResending ? undefined : "Mail"}
          />
        )}
      </div>
    </ErrorPage>
  );
};

export default EmailResendErrorPage;
